import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import type {PostTableColumnSettingsItem} from '../../components/Table/PostTable';
import {safeGetItem, safeRemoveItem, safeSetItem} from '../../utils/safeLocalforage';

export type AppearanceMode = 'system' | 'light' | 'dark';

export const DEFAULT_POST_COLUMNS: PostTableColumnSettingsItem[] = [
  {name: 'numLikes', enable: true},
  {name: 'numComments', enable: true},
  {name: 'numShares', enable: true},
  {name: 'benchmark', enable: true},
  {name: 'numImpressions', enable: true},
  {name: 'numVoters', enable: true},
  {name: 'numUniqueViews', enable: true},
  {name: 'numProfileViewsFromPost', enable: true},
  {name: 'numFollowersFromPost', enable: true},
  {name: 'numSaves', enable: true},
  {name: 'numSentOnLinkedin', enable: true},
  {name: 'numLinkClicks', enable: true},
  {name: 'clicks', enable: true},
  {name: 'engagementRate', enable: false},
];

interface Preferences {
  postColumns: PostTableColumnSettingsItem[];
  appearanceMode: AppearanceMode;
}

const DEFAULT_PREFERENCES: Preferences = {
  postColumns: DEFAULT_POST_COLUMNS,
  appearanceMode: 'system',
};

const STORAGE_KEY = 'perfectpost_preferences';

interface PreferencesValue extends Preferences {
  /** `false` tant que localforage n'a pas répondu : cf. le commentaire ci-dessous. */
  hydrated: boolean;
  setPostColumns: (columns: PostTableColumnSettingsItem[]) => void;
  setAppearanceMode: (mode: AppearanceMode) => void;
}

const PreferencesContext = createContext<PreferencesValue | undefined>(undefined);

const isPreferences = (value: unknown): value is Partial<Preferences> => typeof value === 'object' && value !== null;

/**
 * Reconstruit la liste des colonnes dans l'ordre de `DEFAULT_POST_COLUMNS`.
 *
 * Reprend le reducer `updatePostColumns` et la réconciliation que
 * `getMe.fulfilled` faisait de son côté. Ordre de précédence :
 *
 *   1. le choix reçu (celui de l'utilisateur, ou celui enregistré côté serveur) ;
 *   2. à défaut, le choix local déjà connu ;
 *   3. à défaut, la valeur par défaut de la colonne.
 *
 * Une colonne ajoutée à l'application apparaît donc d'office chez les
 * utilisateurs qui ont déjà des préférences enregistrées, tandis qu'une colonne
 * délibérément masquée le reste. Et une colonne retirée de l'application
 * disparaît de la liste au lieu d'y traîner en fantôme.
 */
export const reconcilePostColumns = (
  current: PostTableColumnSettingsItem[],
  incoming: PostTableColumnSettingsItem[],
): PostTableColumnSettingsItem[] =>
  DEFAULT_POST_COLUMNS.map((defaultColumn) => ({
    name: defaultColumn.name,
    enable:
      incoming.find((column) => column.name === defaultColumn.name)?.enable ??
      current.find((column) => column.name === defaultColumn.name)?.enable ??
      defaultColumn.enable,
  }));

/**
 * Clé écrite par `redux-persist` jusqu'à la migration vers React Query.
 *
 * Elle contenait TOUT `state.main` — l'utilisateur, ses organisations, ses
 * notifications — et pas seulement les préférences. Ces données restent sur le
 * disque de chaque utilisateur tant que personne ne les efface : c'est le
 * dernier artefact de Redux, et il survit au déploiement.
 */
const LEGACY_PERSIST_KEY = 'perfectpost_v2';

/**
 * Récupère les préférences de l'ancien état persisté, une seule fois.
 *
 * `redux-persist` stockait un objet dont CHAQUE champ est sérialisé
 * séparément — `{postColumns: '[...]', customViews: '[...]', user: '{...}'}` —
 * d'où le `JSON.parse` champ par champ.
 *
 * Sans cette reprise, les vues personnalisées du kanban seraient perdues au
 * premier chargement : elles n'ont jamais existé que là, aucun endpoint ne les
 * enregistre (les colonnes, elles, sont aussi côté serveur).
 */
export const readLegacyPreferences = async (): Promise<Partial<Preferences> | undefined> => {
  const legacy = await safeGetItem<unknown>(LEGACY_PERSIST_KEY);
  if (typeof legacy !== 'object' || legacy === null) return undefined;

  const parse = <T,>(field: string): T | undefined => {
    const raw = (legacy as Record<string, unknown>)[field];
    if (typeof raw !== 'string') return undefined;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return undefined;
    }
  };

  // Les vues personnalisées (`customViews`) ne sont plus reprises : elles vivent
  // sur le serveur et sont lues par `hooks/customView` ; la copie locale n'était
  // qu'un miroir écrasé à chaque montage du Kanban.
  const postColumns = parse<PostTableColumnSettingsItem[]>('postColumns');
  if (postColumns === undefined) return undefined;

  return {postColumns: reconcilePostColumns(DEFAULT_POST_COLUMNS, postColumns)};
};

/** Efface l'ancien état persisté, une fois les préférences reprises. */
export const dropLegacyPersistedState = async (): Promise<void> => {
  await safeRemoveItem(LEGACY_PERSIST_KEY);
};

/**
 * Préférences d'affichage persistées : colonnes du tableau de publications,
 * vues personnalisées du kanban, mode d'apparence.
 *
 * CE QU'IL REMPLACE
 *
 * Ces trois valeurs vivaient dans `mainslice`, et c'est POUR ELLES que le store
 * entier passait par `redux-persist` : `persistReducer` sur `main`, un
 * `PersistGate` à la racine de l'application, et localforage en soute. Tout le
 * reste de `state.main` — utilisateur, organisations, notifications — était
 * persisté avec, sans que personne l'ait demandé : de l'état serveur écrit sur
 * disque, réaffiché au démarrage suivant avant d'être écrasé par `GET /me`.
 *
 * Ici, seules les préférences sont persistées, et directement dans localforage.
 * L'ancienne clé est reprise puis effacée au premier chargement (voir
 * `readLegacyPreferences`) : les choix de colonnes et les vues personnalisées
 * survivent au déploiement, le reste ne traîne pas sur le disque.
 *
 * SUR L'HYDRATATION
 *
 * `PersistGate` retardait le rendu de TOUTE l'application le temps de relire le
 * disque. Ce provider rend immédiatement, avec les valeurs par défaut, puis
 * remplace par les valeurs relues. Un seul écran a besoin de savoir : celui qui
 * choisit le thème, sous peine d'afficher un éclair de thème clair avant de
 * passer en sombre. D'où `hydrated`, que `index.tsx` attend avant de peindre.
 */
export function PreferencesProvider({children}: {children: React.ReactNode}) {
  const [preferences, setPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void safeGetItem<unknown>(STORAGE_KEY)
      .then(async (stored) => {
        if (cancelled) return;
        if (isPreferences(stored)) {
          setPreferences({...DEFAULT_PREFERENCES, ...stored});
          await dropLegacyPersistedState();
          return;
        }
        const recovered = await readLegacyPreferences();
        if (cancelled) return;
        if (recovered !== undefined) {
          const next = {...DEFAULT_PREFERENCES, ...recovered};
          setPreferences(next);
          await safeSetItem(STORAGE_KEY, next);
        }
        await dropLegacyPersistedState();
      })
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setPostColumns = useCallback((columns: PostTableColumnSettingsItem[]) => {
    setPreferences((current) => {
      const next = {...current, postColumns: reconcilePostColumns(current.postColumns, columns)};
      void safeSetItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const setAppearanceMode = useCallback((mode: AppearanceMode) => {
    setPreferences((current) => {
      const next = {...current, appearanceMode: mode};
      void safeSetItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({...preferences, hydrated, setPostColumns, setAppearanceMode}),
    [preferences, hydrated, setPostColumns, setAppearanceMode],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesValue {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
